import { useEffect } from 'react';

import { useMutation } from '@apollo/client/react';
import { Typography } from '@mui/material';
import { Trash2Icon } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

import { DELETE_USER, GET_USERS_LIST } from './userQueries';

type Props = {
  userId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
};

export const UserDeleteDialog = ({
  userId,
  open,
  onClose,
  onDeleted,
}: Props) => {
  const { t } = useLocale();

  const [deleteUser, { loading, error, reset }] = useMutation(DELETE_USER, {
    refetchQueries: [GET_USERS_LIST],
    awaitRefetchQueries: true,
    onCompleted: onDeleted,
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  return (
    <>
      <SlideDialog
        open={open}
        onClose={onClose}
        title={t('Delete user')}
        onSave={() => deleteUser({ variables: { user_id: userId } })}
        loading={loading}
        saveText={t('Delete')}
        saveColor="error"
        saveIcon={<Trash2Icon />}
        maxWidth="sm"
        fullWidth
      >
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {t(
            'Are you sure you want to delete this user? Their personal data will be removed and the profile marked as deleted.',
          )}
        </Typography>
      </SlideDialog>
      <MutationErrorSnackbar error={error ?? null} onClose={reset} />
    </>
  );
};
